import React from 'react';
import { FileText, FileSpreadsheet, FileArchive, File, Download } from 'lucide-react';
import { Button } from '../../ui/button';
import { formatFileSize } from '../../../utils/crmChatImageCompress';
import { getCrmChatAssetUrl } from '../../../utils/api';
import { downloadAuthenticatedUrl } from '../../../utils/authenticatedBlobDownload';
import { useToast } from '../../../hooks/use-toast';

function fileIcon(name) {
  const ext = (name || '').split('.').pop()?.toLowerCase();
  if (['pdf', 'doc', 'docx', 'txt', 'ppt', 'pptx'].includes(ext)) {
    return FileText;
  }
  if (['xls', 'xlsx'].includes(ext)) {
    return FileSpreadsheet;
  }
  if (['zip', 'rar'].includes(ext)) {
    return FileArchive;
  }
  return File;
}

export default function CrmChatFileAttachment({ message, isMine }) {
  const { toast } = useToast();
  const name = message.attachment_original_name || 'Document';
  const Icon = fileIcon(name);
  const size = formatFileSize(message.file_size);
  const url = getCrmChatAssetUrl(message.attachment_url);

  const handleDownload = async () => {
    if (!url) return;
    try {
      await downloadAuthenticatedUrl(url, name);
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' });
    }
  };

  return (
    <div className={`flex items-center gap-2 p-2 rounded-xl ${isMine ? 'bg-emerald-700/30' : 'bg-background/60'}`}>
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${isMine ? 'bg-emerald-800/50' : 'bg-muted'}`}>
        <Icon className={`h-5 w-5 ${isMine ? 'text-emerald-100' : 'text-emerald-600'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium truncate ${isMine ? 'text-white' : 'text-foreground'}`}>{name}</div>
        <div className={`text-[11px] ${isMine ? 'text-emerald-100/80' : 'text-muted-foreground'}`}>{size}</div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={`h-8 w-8 shrink-0 ${isMine ? 'text-emerald-100 hover:bg-emerald-700' : ''}`}
        onClick={handleDownload}
        aria-label="Download file"
      >
        <Download className="h-4 w-4" />
      </Button>
    </div>
  );
}
